import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SettlementItemType } from '@euler/model/enum';
import { SettlementForm, SettlementFormItem } from '@euler/model/task-detail';
import { arr2groupmap } from '@euler/utils/array';
import { memo, useMemo } from 'react';
import { SectionList, StyleProp, StyleSheet, ViewStyle } from 'react-native';

type ItemGroup = {
  title: string;
  index: number;
  subTotal: number;
  data: SettlementFormItem[];
};

const kItemTypeMap: { [p in SettlementItemType]: { title: string } } = {
  [SettlementItemType.Construction]: { title: '施工' },
  [SettlementItemType.Material]: { title: '配件' },
  [SettlementItemType.Other]: { title: '其他' },
};

export const SettlementFormView = memo(({ form }: { form: SettlementForm }) => {
  const groups = useMemo<ItemGroup[]>(() => {
    const res: ItemGroup[] = [];
    const items = [...form.items].sort((x, y) => x.sortOrder - y.sortOrder);
    const groupMap = arr2groupmap(items, x => x.subjectName);
    let index = 0;
    for (const [title, list] of groupMap.entries()) {
      const group: ItemGroup = {
        title,
        index: index++,
        subTotal: list.reduce((acc, cur) => acc + cur.subTotal, 0),
        data: list,
      };
      res.push(group);
    }
    return res;
  }, [form.items]);
  return (
    <SectionList
      sections={groups}
      stickySectionHeadersEnabled={false}
      initialNumToRender={50}
      keyExtractor={x => String(x.id)}
      renderSectionHeader={({ section }) => <GroupTitle group={section} />}
      renderItem={({ item }) => <Cell item={item} />}
      contentContainerStyle={{
        flexGrow: 1,
      }}
    />
  );
});

const GroupTitle = memo(
  ({ group, style }: { group: ItemGroup; style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        css={`
          margin-top: ${rpx(15)}px;
          background-color: #fff;
          padding: ${rpx(10)}px ${rpx(15)}px;
        `}
        style={style}
      >
        <Typography.Text bold size={rpx(18)}>{`${group.index + 1}. ${
          group.title
        }`}</Typography.Text>
        <Typography.Text color={StatusColors.Danger}>{`小计: ${(
          group.subTotal / 100
        ).toFixed(2)}元`}</Typography.Text>
      </StackLayout>
    );
  },
);

const Cell = memo(
  ({
    item,
    style,
  }: {
    item: SettlementFormItem;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        style={style}
        css={`
          border-top-width: ${StyleSheet.hairlineWidth}px;
          border-top-color: #d7d7d7;
          background-color: #fff;
          padding: ${rpx(10)}px ${rpx(15)}px;
        `}
      >
        <Typography.Text
          numberOfLines={1}
          ellipsizeMode="middle"
          css={`
            flex: 5;
            flex-shrink: 1;
          `}
        >
          {item.name}
        </Typography.Text>
        <Typography.Text
          align="center"
          numberOfLines={1}
          css={`
            flex: 1;
            flex-shrink: 0;
          `}
        >
          {kItemTypeMap[item.type]?.title ?? '其他'}
        </Typography.Text>
        <Typography.Text
          align="right"
          numberOfLines={1}
          css={`
            flex: 2;
            flex-shrink: 0;
          `}
        >
          {((item.discountPrice ?? item.price) / 100).toFixed(2)}
        </Typography.Text>
        <Typography.Text
          align="right"
          numberOfLines={1}
          css={`
            flex: 1;
            flex-shrink: 0;
          `}
        >
          {item.type === SettlementItemType.Construction ? '' : item.qty}
        </Typography.Text>
      </StackLayout>
    );
  },
);
